import React, { useCallback, useContext, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@atlaskit/button';
import DropdownMenu, {
  DropdownItemGroup,
  DropdownItem,
} from '@atlaskit/dropdown-menu';
import { OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { Extension } from '@atlassian/forge-ui';

import { getDesktopOS } from 'src/components/clone-controls/utils/get-desktop-os';
import { useAnalytics } from 'src/components/settings/analytics';
import {
  ConnectPullRequestModules,
  ConnectPullRequestSummaryActions,
  ConnectPullRequestSummaryInfo,
} from 'src/connect/pull-request-modules';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { LazyForgeRepoPullRequestActionAppsList } from 'src/forge/repo-pull-request-action/lazy-repo-pull-request-action-apps-list';
import { LazyForgeRepoPullRequestActionSingleApp } from 'src/forge/repo-pull-request-action/lazy-repo-pull-request-action-single-app';
import { useFlag } from 'src/hooks/flag';
import { useIntl } from 'src/hooks/intl';
import { START_WATCH, STOP_WATCH } from 'src/redux/pull-request/actions';
import { getIsMergePending } from 'src/redux/pull-request/merge-reducer';
import {
  getCurrentPullRequest,
  getIsAsyncMergeInProgress,
  getIsCurrentPullRequestAuthor,
  getIsCurrentPullRequestOpen,
  getIsSingleFileModeActive,
  getPullRequestCanCurrentUserMerge,
  getPullRequestDraftStatus,
  getPullRequestDraftStatusLoading,
  getWatch,
} from 'src/redux/pull-request/selectors';
import RepositoryAccessGuard from 'src/sections/repository/components/repository-access-guard';
import { getCurrentUserIsAnonymous } from 'src/selectors/user-selectors';

import DeclineDropdownItem from '../../containers/decline-dropdown-item';
import WatchPullRequest from '../../containers/watch-pull-request';
import { useDraftActions } from '../../hooks/draft-actions';
import RevertPullRequest from '../header-actions/revert-pull-request';
import MergeDropdownItem from '../merge-dropdown-item';
import { KeyboardShortcutsContext } from '../tabs/shortcuts/pull-request-keyboard-shortcuts-context';
import ViewInVSCode from '../view-in-vscode';

import { messages } from './header-actions.i18n';
import * as styles from './header-actions.style';

type Props = {
  isHeaderSticky?: boolean;
};

export const DropdownActionsMenu = ({ isHeaderSticky }: Props) => {
  const tabIndex = isHeaderSticky ? -1 : 0;
  const currentPullRequest = useSelector(getCurrentPullRequest);
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);
  const isOpenPr = useSelector(getIsCurrentPullRequestOpen);
  const isCurrentUserAnonymous = useSelector(getCurrentUserIsAnonymous);
  const isCurrentUserAuthor = useSelector(getIsCurrentPullRequestAuthor);
  const canCurrentUserMerge = useSelector(getPullRequestCanCurrentUserMerge);
  const isWatchingPullRequest = useSelector(getWatch);
  const isDesktopOS = Boolean(getDesktopOS());
  const [openedExtension, setOpenedExtension] = useState<Extension>();
  const intl = useIntl();
  const { showFlag } = useFlag();
  const dispatch = useDispatch();
  const { watchPullRequestRef } = useContext(KeyboardShortcutsContext);
  const { markDraft, markReadyForReview } = useDraftActions();
  const areDraftPRsEnabled = useStatsigGate(StatsigFeatureKeys.bbcDraftPRs);
  const isDraftStatusLoading = useSelector(getPullRequestDraftStatusLoading);
  const isDraftPr = useSelector(getPullRequestDraftStatus);
  const isMergePending = useSelector(getIsMergePending);
  const isAsyncMergeInProgress = useSelector(getIsAsyncMergeInProgress);

  const { publishClickEvent } = useAnalytics({
    source: 'pullRequestDropdownActions',
  });

  const handleMarkAsDraft = () => {
    markDraft();
    publishClickEvent({
      name: 'markAsDraftDropdownButton',
      actionSubject: 'button',
    });
  };

  const handleMarkAsReady = () => {
    markReadyForReview();
    publishClickEvent({
      name: 'markAsReadyDropdownButton',
      actionSubject: 'button',
    });
  };

  const toggleWatch = useCallback(
    (isWatching: boolean) => {
      const actionType = isWatching ? STOP_WATCH.BEGIN : START_WATCH.BEGIN;
      const flagId = isWatching
        ? 'not-watching-pull-request'
        : 'watching-pull-request';
      const title = isWatching
        ? messages.notWatchingFlagLabel
        : messages.watchingFlagLabel;

      dispatch({ type: actionType });
      showFlag({
        id: flagId,
        title: { msg: title },
        autoDismiss: true,
      });
    },
    [dispatch, showFlag]
  );

  if (watchPullRequestRef) {
    watchPullRequestRef.current = () => {
      if (!isCurrentUserAnonymous) {
        toggleWatch(isWatchingPullRequest);
      }
    };
  }

  if (isHeaderSticky && isSingleFileModeActive) {
    return null;
  }

  return (
    <styles.DropdownMenu data-qa="pr-header-actions-drop-down-menu-styles">
      <ConnectPullRequestModules />
      <SpotlightTarget name="mark-as-draft">
        <DropdownMenu
          shouldFlip={false}
          placement="bottom-end"
          spacing="compact"
          trigger={({ triggerRef, ...props }) => (
            <Button
              ref={triggerRef}
              {...props}
              iconBefore={
                <MoreIcon
                  label={intl.formatMessage(messages.moreActionsLabel)}
                />
              }
              tabIndex={tabIndex}
            />
          )}
          onOpenChange={(attrs: OnOpenChangeArgs) => {
            if (attrs.isOpen) {
              publishClickEvent({
                name: 'pullrequestHeaderActionClicked',
                actionSubject: 'button',
              });
            }
          }}
        >
          <styles.DropdownMenuContent>
            <DropdownItemGroup>
              <RepositoryAccessGuard requiredLevel="write">
                {isOpenPr && !isDraftStatusLoading && !isDraftPr && (
                  <>
                    {/* Only display the "Merge" button to non-authors with merge privileges*/}
                    {!isCurrentUserAuthor && canCurrentUserMerge && (
                      <MergeDropdownItem />
                    )}
                    {areDraftPRsEnabled &&
                      !isMergePending &&
                      !isAsyncMergeInProgress && (
                        <DropdownItem onClick={handleMarkAsDraft}>
                          <FormattedMessage {...messages.markAsDraftAction} />
                        </DropdownItem>
                      )}
                  </>
                )}
                {isOpenPr &&
                  !isDraftStatusLoading &&
                  isDraftPr &&
                  !isCurrentUserAuthor &&
                  areDraftPRsEnabled && (
                    <DropdownItem onClick={handleMarkAsReady}>
                      <FormattedMessage {...messages.markAsReadyAction} />
                    </DropdownItem>
                  )}
                {isOpenPr && (
                  <DeclineDropdownItem>
                    <FormattedMessage {...messages.declinePullRequestAction} />
                  </DeclineDropdownItem>
                )}
                <RevertPullRequest />
              </RepositoryAccessGuard>
              {!isCurrentUserAnonymous && (
                <WatchPullRequest toggleWatch={toggleWatch} />
              )}
              <ConnectPullRequestSummaryInfo component={DropdownItem} />
              <ConnectPullRequestSummaryActions component={DropdownItem} />
              {isDesktopOS && currentPullRequest && (
                <ViewInVSCode
                  currentPullRequestHref={currentPullRequest.links.html.href}
                />
              )}
              <LazyForgeRepoPullRequestActionAppsList
                onAppClick={app => setOpenedExtension(app)}
              />
            </DropdownItemGroup>
          </styles.DropdownMenuContent>
        </DropdownMenu>
      </SpotlightTarget>
      <LazyForgeRepoPullRequestActionSingleApp
        pullRequest={currentPullRequest}
        onAppClose={() => setOpenedExtension(undefined)}
        openedExtension={openedExtension}
      />
    </styles.DropdownMenu>
  );
};

export default React.memo(DropdownActionsMenu);
